import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { allArticles } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setMobileGuideOpen(false);
  }, [location.pathname]);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const isTransparent = isHome && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className={`text-lg md:text-xl font-heading font-bold tracking-tight whitespace-nowrap cursor-pointer ${
              isTransparent ? 'text-white' : 'text-foreground-900'
            }`}
          >
            Pandabuy<span className={isTransparent ? 'text-primary-300' : 'text-primary-500'}>Spreadsheet</span>
          </Link>

          <div className="hidden md:flex items-center gap-7">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:text-primary-500 ${
                isTransparent ? 'text-white/90' : 'text-foreground-700'
              }`}
            >
              Home
            </Link>

            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:text-primary-500 ${
                  isTransparent ? 'text-white/90' : 'text-foreground-700'
                }`}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background-50 rounded-xl border border-background-200/70 py-2 min-w-[240px] max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 whitespace-nowrap cursor-pointer transition-colors"
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded bg-background-100 text-foreground-400 text-xs">
                        <i className="ri-arrow-right-up-line"></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/guide/pandabuy-sheet-guide"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:text-primary-500 ${
                isTransparent ? 'text-white/90' : 'text-foreground-700'
              }`}
            >
              Shopping Guide
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all ${
                isTransparent
                  ? 'bg-white/15 backdrop-blur-sm text-white border border-white/25 hover:bg-white/25'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
            >
              Pandabuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all ${
                isTransparent
                  ? 'bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30'
                  : 'bg-foreground-900 text-white hover:bg-foreground-800'
              }`}
            >
              Buy It Now
              <i className="ri-shopping-cart-line"></i>
            </a>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg ${
              isTransparent ? 'text-white' : 'text-foreground-900'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={mobileMenuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[70vh] overflow-y-auto">
            <div className="py-3 px-2">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                  <i className="ri-home-line"></i>
                </span>
                Home
              </Link>

              <button
                className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors"
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="ml-4 pl-4 border-l-2 border-background-200/60 mt-1 mb-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors rounded-lg hover:bg-background-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-2 h-2 rounded-full bg-primary-300 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <button
                className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors"
                onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                    <i className="ri-book-open-line"></i>
                  </span>
                  Shopping Guides
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuideOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileGuideOpen && (
                <div className="ml-4 pl-4 border-l-2 border-background-200/60 mt-1 mb-1">
                  {allArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/guide/${article.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors rounded-lg hover:bg-background-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-2 h-2 rounded-full bg-accent-300 flex-shrink-0"></span>
                      {article.tag}
                    </Link>
                  ))}
                </div>
              )}

              <div className="border-t border-background-200/60 mt-3 pt-3 px-4 flex flex-col gap-2">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
                >
                  Pandabuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-foreground-900 text-white hover:bg-foreground-800 whitespace-nowrap cursor-pointer transition-colors"
                >
                  Buy It Now
                  <i className="ri-shopping-cart-line"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}